'use client';

import React, { createContext, useContext, useMemo } from 'react';
import useGetImageUrlsByFolders from '@/components/hooks/use-get-image-urls-by-folders';
import useGetSubFolders from '@/components/hooks/use-get-sub-folders';
import type {
  ProjectsProviderProperties,
  ProjectsProviderState,
} from './types';

const INITIAL_PROVIDER_STATE: ProjectsProviderState = {
  folderError: null,
  folders: [],
  imageUrlErrors: null,
  images: {},
  isLoading: true,
};

const ProjectsStateContext = createContext(INITIAL_PROVIDER_STATE);

export function useProjectsStateContext() {
  return useContext(ProjectsStateContext);
}

function ProjectsProvider({ children }: ProjectsProviderProperties) {
  const getSubFoldersQuery = useGetSubFolders({ path: 'projects' });

  const getImageUrlsByFoldersQuery = useGetImageUrlsByFolders({
    folders: getSubFoldersQuery.data ?? [],
    hasFolders: !getSubFoldersQuery.error,
    maxResults: 1,
    tags: 'main',
  });

  const isLoading = useMemo(
    () =>
      getSubFoldersQuery.isLoading ||
      getImageUrlsByFoldersQuery.some(q => q.isLoading),
    [getSubFoldersQuery.isLoading, getImageUrlsByFoldersQuery],
  );

  const imageUrlErrors = useMemo(() => {
    const errors = getImageUrlsByFoldersQuery.reduce(
      (accumulator, current, index) => {
        if (current.error) {
          return {
            ...accumulator,
            [index]: current.error.message,
          };
        }

        return accumulator;
      },
      {},
    );

    if (Object.keys(errors).length === 0) {
      return null;
    }

    return errors;
  }, [getImageUrlsByFoldersQuery]);

  const images = useMemo(
    () =>
      getImageUrlsByFoldersQuery.reduce(
        (accumulator, current) => ({ ...accumulator, ...current.data }),
        {},
      ),
    [getImageUrlsByFoldersQuery],
  );

  const context = useMemo(
    () => ({
      folderError: getSubFoldersQuery.error?.message ?? null,
      folders: getSubFoldersQuery.data,
      imageUrlErrors,
      images,
      isLoading,
    }),
    [
      getSubFoldersQuery.data,
      getSubFoldersQuery.error,
      images,
      isLoading,
      imageUrlErrors,
    ],
  );

  return (
    <ProjectsStateContext.Provider value={context}>
      {children}
    </ProjectsStateContext.Provider>
  );
}

export default ProjectsProvider;
